import React, { FC } from "react";
import { useNavigate } from "react-router-dom";
import { Column, DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Edit } from "lucide-react";
import { GuestWithLastBooking } from "@/types/guest";
import { Checkbox } from "@/components/ui/checkbox";
import { GuestNameWithFlag } from "./GuestNameWithFlag";
import { PresignedImageWithPlaceholder } from "../../../../common/PresignedImageWithPlaceholder";
import userPhotoPlaceholder from "../../../../../../public/user-photo-placeholder.png";
import GuestModal from "@/components/pages/reservations/guests/components/GuestModal";
import { GuestFormData } from "../utils/guestSchema";
import { useGuestUpdate } from "@/components/pages/reservations/guests/hooks/useGuestUpdate";
import { useCheckboxSelection } from "@/hooks/useCheckboxSelection";
import { DateCell } from "@/components/pages/settlement/components/table-cells/DateCell";
import { LinkCell } from "@/components/pages/settlement/components/table-cells/LinkCell";
import { StatusCell } from "@/components/pages/settlement/components/table-cells/StatusCell";
import { determineGuestStatus } from "../utils/guestStatusUtils";
import { GuestStatus } from "@/types/guest";
import { MessageGuestButton } from "@/components/pages/chats/component/MessageGuestButton";
import { CreateGuestRequestButton } from "./CreateGuestRequestButton";
import { useTranslation } from "react-i18next";
import { NotesPopoverCell } from "@/components/common/NotesPopoverCell";
import { getGuestProfileNotes } from "../utils/guestPreferences";

interface GuestsTableProps {
  data: GuestWithLastBooking[];
  sortField: string | null;
  sortDirection: "asc" | "desc" | null;
  onSort: (field: string) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const GuestsTable: FC<GuestsTableProps> = ({
  data,
  sortField,
  sortDirection,
  onSort,
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const navigate = useNavigate();
  const { handleGuestUpdate } = useGuestUpdate();
  const { t } = useTranslation();

  const {
    selectAll,
    toggleSelectAll,
    toggleSelectItem: toggleSelectGuest,
    isSelected,
  } = useCheckboxSelection({
    data,
    getId: (guest) => guest.id,
  });

  const handleRowClick = (guest: GuestWithLastBooking) => {
    navigate(`/reservations/guests/${guest.id}`);
  };

  const columns: Column<GuestWithLastBooking>[] = [
    {
      key: "select",
      header: (
        <Checkbox
          className={"size-5 border-zinc-900"}
          checked={selectAll}
          onCheckedChange={toggleSelectAll}
          aria-label={t("reservation.guestsTable.selectAll")}
        />
      ),
      className: "text-center w-10",
      render: (_, row) => (
        <Checkbox
          className={"size-5 border-zinc-900"}
          checked={isSelected(row.id)}
          onCheckedChange={() => {
            const syntheticEvent = {
              stopPropagation: () => {},
            } as React.MouseEvent;
            toggleSelectGuest(row.id, syntheticEvent);
          }}
          onClick={(e) => e.stopPropagation()}
          aria-label={t("reservation.guestsTable.selectGuest", {
            name:
              `${row.profile.firstName || ""} ${row.profile.lastName || ""}`.trim() ||
              t("reservation.guestsTable.guestFallback"),
          })}
        />
      ),
    },
    {
      key: "photo",
      header: t("reservation.table.photo"),
      className: "text-center",
      render: (_, row) => (
        <PresignedImageWithPlaceholder
          className={"mx-auto h-8 w-8 rounded-full object-cover"}
          photo={row.profile.photoId}
          placeholder={
            <img
              src={userPhotoPlaceholder}
              alt={t("reservation.guestsTable.userPhotoPlaceholder")}
              className="mx-auto h-8 w-8 rounded-full object-cover"
            />
          }
          alt={`${row.profile.firstName} ${row.profile.lastName}`}
        />
      ),
    },
    {
      key: "firstName",
      header: t("reservation.table.guestName"),
      className: "text-center",
      sortable: true,
      render: (_, row) => (
        <GuestNameWithFlag
          name={
            row.profile.firstName || row.profile.lastName
              ? `${row.profile.firstName} ${row.profile.lastName}`
              : "-"
          }
          countryCode={row.profile.language}
        />
      ),
    },
    {
      key: "phone",
      header: t("reservation.form.phone"),
      className: "text-center",
      render: (_, row) => {
        const phone = row?.profile.phone?.trim();

        if (!phone) return "-";

        const telValue = phone.replace(/\s+/g, "");

        return (
          <a
            href={`tel:${telValue}`}
            onClick={(event) => event.stopPropagation()}
            className="text-primary underline-offset-2 hover:underline focus:underline"
            aria-label={t("reservation.guestsTable.callGuest", {
              name:
                row.profile.firstName ||
                t("reservation.guestsTable.guestFallback"),
            })}
          >
            {phone}
          </a>
        );
      },
    },
    {
      key: "email",
      header: t("reservation.form.email"),
      className: "text-center",
      render: (_, row) => {
        const email = row?.profile.email?.trim();

        if (!email) return "-";

        return (
          <a
            href={`mailto:${email}`}
            onClick={(event) => event.stopPropagation()}
            className="text-primary underline-offset-2 hover:underline focus:underline"
            aria-label={t("reservation.guestsTable.emailGuest", {
              name:
                row.profile.firstName ||
                t("reservation.guestsTable.guestFallback"),
            })}
          >
            {email}
          </a>
        );
      },
    },
    {
      key: "guest_status",
      header: t("reservation.table.guestStatus"),
      className: "text-center",
      render: (_, row) => {
        const guestStatus = determineGuestStatus(row?.lastBooking);
        return (
          <StatusCell
            statuses={[
              {
                status: GuestStatus.IN_HOUSE,
                color: "border-accent-green text-accent-green",
                text: t("reservation.guestsTable.status.inHouse"),
              },
              {
                status: GuestStatus.PRE_CHECKIN,
                color: "border-blue-500 text-blue-500",
                text: t("reservation.guestsTable.status.preCheckin"),
              },
              {
                status: GuestStatus.PRE_CHECKOUT,
                color: "border-orange-500 text-orange-500",
                text: t("reservation.guestsTable.status.preCheckout"),
              },
              {
                status: GuestStatus.BEFORE_CHECK_IN,
                color: "border-yellow-500 text-yellow-500",
                text: t("reservation.guestsTable.status.beforeCheckin"),
              },
              {
                status: GuestStatus.BEFORE_CHECK_OUT,
                color: "border-red-500 text-red-500",
                text: t("reservation.guestsTable.status.beforeCheckout"),
              },
            ]}
            currentStatus={guestStatus!}
          />
        );
      },
    },
    {
      key: "room",
      header: t("reservation.table.rooms"),
      className: "text-center",
      render: (_, row) => (
        <LinkCell
          href={`/reservations/rooms/${row?.lastBooking?.room?.id}`}
          label={String(row?.lastBooking?.room?.number ?? "")}
        />
      ),
    },
    {
      key: "arrivalDate",
      header: t("reservation.table.arrival"),
      className: "text-center",
      render: (_, row) => (
        <>
          {row?.lastBooking?.arrivalDate ? (
            <DateCell date={row.lastBooking.arrivalDate} />
          ) : (
            "-"
          )}
        </>
      ),
    },
    {
      key: "departure",
      header: t("reservation.table.departure"),
      className: "text-center",
      render: (_, row) => (
        <>
          {row?.lastBooking?.departureDate ? (
            <DateCell date={row.lastBooking.departureDate} />
          ) : (
            "-"
          )}
        </>
      ),
    },
    {
      key: "accommodation",
      header: t("reservation.guestsTable.accommodationPreferences"),
      className: "text-center",
      render: (_, row) => row?.profile.preferences?.accommodation || "-",
      sortable: true,
    },
    {
      key: "notes",
      header: t("reservation.table.notes"),
      className: "text-center",
      render: (_, row) => {
        const notes = getGuestProfileNotes(row.profile.preferences);

        return (
          <NotesPopoverCell
            notes={notes}
            title={t("reservation.notesSection.guestNotes")}
            emptyMessage={t("reservation.notesSection.empty")}
            ariaLabel={t("reservation.notesSection.guestNotesAria", {
              count: notes.length,
            })}
          />
        );
      },
      sortable: true,
    },
    {
      key: "actions",
      header: t("reservation.table.actions"),
      className: "text-center",
      render: (_, row) => (
        <div className="flex items-center justify-center gap-2">
          {row.lastBooking?.id && (
            <MessageGuestButton bookingId={row.lastBooking.id} />
          )}
          <CreateGuestRequestButton guest={row} />
          <GuestModal
            guestId={row.id}
            trigger={
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                <Edit className="h-4 w-4" />
              </Button>
            }
            onSubmit={(guestData: GuestFormData, photoFile?: File) =>
              handleGuestUpdate(
                row.id,
                guestData,
                row.profile.photoId,
                photoFile,
              )
            }
          />
        </div>
      ),
    },
  ];

  return (
    <DataTable
      data={data}
      columns={columns}
      loading={false}
      pagination={{
        currentPage,
        totalPages,
        onPageChange,
      }}
      sorting={{
        field: sortField || "",
        direction: sortDirection || "asc",
        onSort,
      }}
      emptyMessage={t("reservation.guestsTable.noGuestsFound")}
      onRowClick={handleRowClick}
    />
  );
};
